import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiClock, FiStar, FiMapPin } from 'react-icons/fi';

const FeaturesSection = styled.section`
  background: #f8fafb;
  width: 100%;
  padding: 100px 20px;
  overflow: hidden;
  direction: rtl;
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
`;

const Eyebrow = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  color: #DA015C;
  margin-bottom: 16px;
  text-align: center;
`;

const SectionHeading = styled.h2`
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 900;
  color: #0a174e;
  text-align: center;
  letter-spacing: -0.02em;
  line-height: 1.1;
  margin-bottom: 72px;
  span { color: #DA015C; }
`;

const FeatureBlock = styled.div<{ reverse?: boolean }>`
  display: flex;
  flex-direction: ${p => p.reverse ? 'row-reverse' : 'row'};
  align-items: center;
  gap: 80px;
  margin-bottom: 100px;
  &:last-child { margin-bottom: 0; }
  @media (max-width: 860px) {
    flex-direction: column;
    gap: 40px;
    margin-bottom: 64px;
    text-align: center;
  }
`;

const TextBlock = styled(motion.div)`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  @media (max-width: 860px) { align-items: center; }
`;

const TagBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: rgba(10,23,78,0.06);
  border-radius: 100px;
  padding: 5px 14px;
  font-size: 0.78rem;
  font-weight: 700;
  color: #0a174e;
  margin-bottom: 20px;
`;

const FeatureTitle = styled.h3`
  font-size: clamp(1.6rem, 3vw, 2.2rem);
  font-weight: 900;
  color: #0a174e;
  letter-spacing: -0.02em;
  line-height: 1.15;
  margin-bottom: 16px;
  span { color: #DA015C; }
`;

const FeatureDesc = styled.p`
  font-size: 1.05rem;
  color: #5a6a7c;
  line-height: 1.8;
  max-width: 460px;
  margin-bottom: 28px;
  @media (max-width: 860px) { max-width: 100%; }
`;

const BulletList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const BulletItem = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.95rem;
  font-weight: 600;
  color: #0a174e;
  @media (max-width: 860px) { justify-content: center; }
`;

const BulletDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #DA015C;
  flex-shrink: 0;
`;

const ImageBlock = styled(motion.div)`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const FeatureImg = styled.img`
  width: 100%;
  max-width: 480px;
  height: auto;
  border-radius: 20px;
  box-shadow: 0 24px 60px rgba(10,23,78,0.12);
  object-fit: cover;
  display: block;
`;

const ImgAccent = styled.div<{ top?: string; bottom?: string; left?: string; right?: string }>`
  position: absolute;
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: linear-gradient(135deg, #DA015C, #0a174e);
  opacity: 0.12;
  top: ${p => p.top};
  bottom: ${p => p.bottom};
  left: ${p => p.left};
  right: ${p => p.right};
  z-index: 0;
`;

const features = [
  {
    tag: '⚡ תמיד מוכן',
    title: <>זמין <span>24 שעות</span>, 7 ימים בשבוע</>,
    desc: 'בין אם זה נסיעה לעבודה בבוקר המוקדם או חזרה הביתה בלילה — וואסל תמיד כאן כשאתה צריך.',
    bullets: ['שיבוץ נהג מיידי', 'ללא הגבלות שעות', 'זמינות בזמן אמת'],
    image: '/images/app-booking.png',
    reverse: false,
  },
  {
    tag: '🏅 נהגים מובחרים',
    title: <>נהגים <span>מקצועיים ומאומתים</span></>,
    desc: 'כל נהג וואסל עובר בדיקת רקע מקיפה והכשרה מקצועית. ציפה לרכבים נקיים, שירות מנומס ונסיעה חלקה.',
    bullets: ['בדיקת רקע מלאה', 'דירוג לאחר כל נסיעה', 'מוכשרים ומקצועיים'],
    image: '/images/wassel-driver.jpg',
    reverse: true,
  },
  {
    tag: '📍 תמיד מעודכן',
    title: <>מעקב GPS <span>חי בכל נסיעה</span></>,
    desc: 'עקוב אחר הנהג בזמן אמת על המפה. שתף את מסלול הנסיעה עם בני המשפחה ודע בדיוק מתי תגיע.',
    bullets: ['מעקב מפה בזמן אמת', 'שיתוף נסיעה עם קרובים', 'עדכוני זמן הגעה מדויקים'],
    image: '/images/Real-time-Tracking.png',
    reverse: false,
  },
];

const FeaturesHe: React.FC = () => (
  <FeaturesSection>
    <Inner>
      <Eyebrow>איך זה עובד</Eyebrow>
      <SectionHeading>כל מה שצריך, <span>בלי מה שלא</span></SectionHeading>
      {features.map((f, i) => (
        <FeatureBlock key={i} reverse={f.reverse}>
          <TextBlock
            initial={{ opacity: 0, x: f.reverse ? -40 : 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: 'easeOut' }}
          >
            <TagBadge>{f.tag}</TagBadge>
            <FeatureTitle>{f.title}</FeatureTitle>
            <FeatureDesc>{f.desc}</FeatureDesc>
            <BulletList>
              {f.bullets.map((b, j) => (
                <BulletItem key={j}><BulletDot />{b}</BulletItem>
              ))}
            </BulletList>
          </TextBlock>
          <ImageBlock
            initial={{ opacity: 0, x: f.reverse ? 40 : -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
          >
            <ImgAccent top="-20px" right="-20px" />
            <ImgAccent bottom="-20px" left="-20px" />
            <FeatureImg src={f.image} alt="" loading="lazy" />
          </ImageBlock>
        </FeatureBlock>
      ))}
    </Inner>
  </FeaturesSection>
);

export default FeaturesHe;
